import { supabase } from './supabaseClient';

/** localStorage形式（camelCase）→ DB形式（snake_case）に変換 */
function toRow(record, childId) {
  // idがnull/undefinedの場合（古い形式のきろく）は自動生成
  const id = record.id || `rec_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  return {
    id,
    child_id:    childId,
    title:       record.title      ?? null,
    mode:        record.mode       ?? null,
    avg_score:   record.avgScore   ?? null,
    total_stars: record.totalStars ?? null,
    local_date:  record.localDate  ?? null,
    transcript:  record.display ?? record.transcript ?? null, // display = convertForDisplay済みのひらがな変換後テキスト
    ai_feedback: record.feedback   ?? null,
    ai_details:  record.details    ?? null,
    assignment_id: record.assignmentId ?? null, // どの配布課題の提出か（生徒ページで絞り込むため）
  };
}

/** DB形式→ localStorage形式に変換 */
function fromRow(row) {
  return {
    id:         row.id,
    title:      row.title,
    mode:       row.mode,
    avgScore:   row.avg_score,
    totalStars: row.total_stars,
    localDate:  row.local_date,
    transcript: row.transcript  ?? null,
    aiFeedback: row.ai_feedback ?? null,
    aiDetails:  row.ai_details  ?? null,
    assignmentId: row.assignment_id ?? null,
  };
}

/** 読み終わり後に1件保存（ログイン済みのみ） */
export async function saveLog(record, childId, isPremium = false) {
  if (!childId) return;
  const { error } = await supabase
    .from('reading_logs')
    .upsert(toRow(record, childId), { onConflict: 'id' });
  if (error) console.warn('reading_logs保存エラー:', error);
  // ※ transcript/ai_feedback/ai_details はテキストデータ（軽量）なので件数制限しない。
  //    音声ファイル（Storage）側だけ件数制限することでコストを抑える。
}

/** child_idに紐づくきろくを全件取得（新しい順） */
export async function getLogs(childId) {
  if (!childId) return null; // null=未ログイン
  const { data, error } = await supabase
    .from('reading_logs')
    .select('*')
    .eq('child_id', childId)
    .order('local_date', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) {
    console.warn('reading_logs取得エラー:', error);
    return null;
  }
  return (data || []).map(fromRow);
}

/** Supabaseのきろくからホーム画面用の統計・カレンダーデータを計算 */
export async function getCloudStats(childId) {
  const logs = await getLogs(childId);
  if (!logs) return null;

  // カレンダー用: 日付→最高スター数のマップ
  const stampMap = {};
  logs.forEach((r) => {
    if (!r.localDate) return;
    const stars = r.totalStars ?? 0;
    if (stampMap[r.localDate] === undefined || stars > stampMap[r.localDate]) {
      stampMap[r.localDate] = stars;
    }
  });

  // 今日の日付（ローカル時刻）
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const thisMonth = todayStr.slice(0, 7);

  // 連続日数
  const dates = [...new Set(logs.map((r) => r.localDate).filter(Boolean))].sort();
  let streak = 0;
  let check = todayStr;
  for (let i = dates.length - 1; i >= 0; i--) {
    if (dates[i] === check) {
      streak++;
      const d = new Date(check + 'T12:00:00'); // 正午基準でUTCズレを回避
      d.setDate(d.getDate() - 1);
      check = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    } else {
      break;
    }
  }

  // 今月の読んだ日数
  const monthDays = new Set(
    logs.filter((r) => r.localDate?.startsWith(thisMonth)).map((r) => r.localDate)
  ).size;

  // 合計スター
  const totalStars = logs.reduce((s, r) => s + (r.totalStars ?? 0), 0);

  return { streak, totalStars, monthDays, stampMap };
}

/** ゲスト時のlocalStorageきろくをSupabaseに一括移行 */
export async function migrateLogs(records, childId) {
  if (!childId || !records || records.length === 0) return;
  const rows = records.map((r) => toRow(r, childId));
  const { error } = await supabase
    .from('reading_logs')
    .upsert(rows, { onConflict: 'id' });
  if (error) console.warn('reading_logs移行エラー:', error);
  else console.log(`reading_logs: ${rows.length}件をクラウドに移行しました`);
}
